/**
 * Cross-tab browser session refresh (HttpOnly refresh_token cookie).
 */

import { analytics } from '@/lib/analytics';
import { getApiV1BaseUrl } from '@/lib/api-config';
import { ACCESS_TOKEN_REFRESH_BUFFER_MS, isJwtExpiredOrExpiringSoon } from '@/lib/jwt-client';
import { getBearerTokenSync } from '@/lib/auth-token';
import { getSessionEpoch, isSessionEpochCurrent } from '@/lib/session-epoch';
import { withRefreshLock } from '@/lib/session-refresh-lock';
import { useAuth } from '@/stores/use-auth';

const REFRESH_BROADCAST_CHANNEL = 'codemerlin-auth-refresh';

/** Minimum interval between proactive refreshes when no Bearer is in memory (cookie-only). */
export const PROACTIVE_REFRESH_THROTTLE_MS = 10 * 60 * 1000;

interface RefreshResponse {
  accessToken?: string;
  expiresIn?: number;
  needsOrgChoice?: boolean;
}

interface RefreshBroadcastMessage {
  type: 'session-refreshed';
  accessToken: string;
  sessionEpoch: number;
}

let refreshInFlight: Promise<boolean> | null = null;

/** True while a coordinated refresh request is in flight (any tab). */
export function isRefreshBrowserSessionInFlight(): boolean {
  return refreshInFlight !== null;
}

/** Await an in-flight refresh without starting a new one. */
export function getRefreshBrowserSessionPromise(): Promise<boolean> | null {
  return refreshInFlight;
}
let broadcastChannel: BroadcastChannel | null = null;
let broadcastListenerAttached = false;
let lastProactiveRefreshAt = 0;
/** Limits serial refresh storms when many 401s fire in a redirect loop. */
let lastReactiveRefreshSucceededAt = 0;
export const REACTIVE_REFRESH_COOLDOWN_MS = 3000;

function shouldSkipReactiveRefreshHttp(force: boolean): boolean {
  if (!force) {
    return false;
  }
  if (
    lastReactiveRefreshSucceededAt <= 0 ||
    Date.now() - lastReactiveRefreshSucceededAt >= REACTIVE_REFRESH_COOLDOWN_MS
  ) {
    return false;
  }
  const token = getBearerTokenSync();
  return Boolean(token && !isJwtExpiredOrExpiringSoon(token, ACCESS_TOKEN_REFRESH_BUFFER_MS));
}

/** Clear refresh throttles after logout so stale cooldown cannot block the next sign-in. */
export function resetSessionRefreshCoordinationOnLogout(): void {
  lastReactiveRefreshSucceededAt = 0;
  lastProactiveRefreshAt = 0;
}

function getRefreshUrl(): string {
  return `${getApiV1BaseUrl()}/auth/refresh`;
}

function applySessionToken(
  accessToken: string,
  sessionEpoch: number,
  options?: { allowBootstrap?: boolean }
): boolean {
  if (!isSessionEpochCurrent(sessionEpoch)) {
    return false;
  }
  if (!options?.allowBootstrap && !useAuth.getState().isAuthenticated) {
    return false;
  }
  useAuth.getState().setSessionToken(accessToken);
  return true;
}

export function detachSessionRefreshBroadcast(): void {
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  broadcastListenerAttached = false;
}

function attachRefreshBroadcastListener(): void {
  if (broadcastListenerAttached || typeof BroadcastChannel === 'undefined') {
    return;
  }
  broadcastChannel = new BroadcastChannel(REFRESH_BROADCAST_CHANNEL);
  broadcastChannel.addEventListener('message', (event: MessageEvent<RefreshBroadcastMessage>) => {
    if (event.origin !== window.location.origin) {
      return;
    }
    if (event.data?.type !== 'session-refreshed') {
      return;
    }
    const { accessToken, sessionEpoch } = event.data;
    if (typeof accessToken !== 'string' || accessToken.length === 0) {
      return;
    }
    if (typeof sessionEpoch !== 'number' || !Number.isFinite(sessionEpoch)) {
      return;
    }
    applySessionToken(accessToken, sessionEpoch);
  });
  broadcastListenerAttached = true;
}

function broadcastSessionRefreshed(accessToken: string, sessionEpoch: number): void {
  if (typeof BroadcastChannel === 'undefined') {
    return;
  }
  attachRefreshBroadcastListener();
  broadcastChannel?.postMessage({
    type: 'session-refreshed',
    accessToken,
    sessionEpoch,
  } satisfies RefreshBroadcastMessage);
}

/**
 * Whether a proactive refresh should run (respects throttle when no in-memory JWT).
 */
export function shouldRunProactiveRefresh(): boolean {
  if (!useAuth.getState().isAuthenticated) {
    return false;
  }
  const token = getBearerTokenSync();
  if (token) {
    return isJwtExpiredOrExpiringSoon(token, ACCESS_TOKEN_REFRESH_BUFFER_MS);
  }
  return Date.now() - lastProactiveRefreshAt >= PROACTIVE_REFRESH_THROTTLE_MS;
}

async function performRefreshRequest(
  trigger: 'reactive' | 'proactive',
  sessionEpoch: number,
  allowBootstrap: boolean
): Promise<boolean> {
  if (!isSessionEpochCurrent(sessionEpoch)) {
    return false;
  }
  if (!allowBootstrap && !useAuth.getState().isAuthenticated) {
    return false;
  }

  analytics.capture('auth_session_refresh_attempted');

  const response = await fetch(getRefreshUrl(), {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  let data: RefreshResponse | null = null;
  try {
    const refreshText = await response.text();
    data = refreshText.trim() ? (JSON.parse(refreshText) as RefreshResponse) : null;
  } catch {
    data = null;
  }

  if (!response.ok) {
    analytics.capture('auth_session_refresh_failed', {
      status: response.status,
    });
    return false;
  }

  const tokenOk =
    data &&
    typeof data.accessToken === 'string' &&
    data.accessToken.length > 0 &&
    typeof data.expiresIn === 'number' &&
    Number.isFinite(data.expiresIn) &&
    data.expiresIn > 0;

  if (!tokenOk) {
    analytics.capture('auth_session_refresh_failed', {
      status: response.status,
      reason: 'invalid_refresh_payload',
    });
    return false;
  }

  if (!isSessionEpochCurrent(sessionEpoch)) {
    return false;
  }
  if (!allowBootstrap && !useAuth.getState().isAuthenticated) {
    return false;
  }

  const payload = data as RefreshResponse & { accessToken: string; expiresIn: number };
  if (!applySessionToken(payload.accessToken, sessionEpoch, { allowBootstrap })) {
    return false;
  }

  if (trigger === 'proactive') {
    lastProactiveRefreshAt = Date.now();
  }

  broadcastSessionRefreshed(payload.accessToken, sessionEpoch);

  if (trigger === 'reactive') {
    lastReactiveRefreshSucceededAt = Date.now();
  }

  analytics.capture('auth_session_refresh_succeeded', {
    needs_org_choice: Boolean(payload.needsOrgChoice),
    trigger,
  });
  return true;
}

/**
 * Refresh the browser session using the HttpOnly refresh_token cookie.
 * Coordinates across tabs via Web Locks + BroadcastChannel.
 */
export async function refreshBrowserSession(options?: {
  trigger?: 'reactive' | 'proactive';
  /** When true, refresh even if the in-memory token still looks valid (401 recovery). */
  force?: boolean;
  /** When true, refresh using the HttpOnly cookie even if the auth store is logged out (sign-in restore). */
  allowBootstrap?: boolean;
}): Promise<boolean> {
  const trigger = options?.trigger ?? 'reactive';
  const force = options?.force ?? false;
  const allowBootstrap = options?.allowBootstrap ?? false;

  if (trigger === 'proactive' && !force && !shouldRunProactiveRefresh()) {
    return true;
  }

  if (!allowBootstrap && !useAuth.getState().isAuthenticated) {
    return false;
  }

  if (refreshInFlight) {
    return refreshInFlight;
  }

  if (trigger === 'reactive' && shouldSkipReactiveRefreshHttp(force)) {
    return true;
  }

  const sessionEpoch = getSessionEpoch();
  attachRefreshBroadcastListener();

  const refreshPromise = withRefreshLock(async () => {
    if (!isSessionEpochCurrent(sessionEpoch)) {
      return false;
    }
    if (!force) {
      const token = getBearerTokenSync();
      if (token && !isJwtExpiredOrExpiringSoon(token, ACCESS_TOKEN_REFRESH_BUFFER_MS)) {
        return true;
      }
    }
    if (trigger === 'reactive' && shouldSkipReactiveRefreshHttp(force)) {
      return true;
    }
    return performRefreshRequest(trigger, sessionEpoch, allowBootstrap);
  }).finally(() => {
    refreshInFlight = null;
  });

  refreshInFlight = refreshPromise;
  return refreshPromise;
}

/** Reset proactive/reactive throttles (tests). */
export function resetProactiveRefreshThrottleForTests(): void {
  resetSessionRefreshCoordinationOnLogout();
}
